import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const termsStructuredData = [
  {
    "@type": "WebPage",
    name: "利用規約",
    description: "ハヤブサワークス公式サイトの利用規約。禁止事項、著作権、免責事項、商品購入に関する注意事項を定めています。",
    url: "https://raigeki-hayabusa.com/terms",
    inLanguage: "ja",
    isPartOf: {
      "@type": "WebSite",
      name: "ハヤブサワークス",
      url: "https://raigeki-hayabusa.com",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ホーム",
          item: "https://raigeki-hayabusa.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "利用規約",
        },
      ],
    },
  },
];

export default function Terms() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100">
      <SEOHead
        title="利用規約｜ハヤブサワークス"
        description="ハヤブサワークス公式サイトの利用規約。禁止事項、著作権、免責事項、商品購入に関する注意事項を定めています。"
        keywords="利用規約,ハヤブサワークス"
        canonicalPath="/terms"
        ogImage="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/cfb5afee631d88a7b12672494ae842fb.jpeg"
        structuredData={termsStructuredData}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20abstract%20light%20background%20with%20soft%20warm%20toned%20paper%20texture%20in%20cream%20and%20light%20beige%2C%20gentle%20natural%20light%20from%20top%20right%2C%20clean%20empty%20center%20area%2C%20subtle%20grain%20texture%2C%20artistic%20minimal%20composition%2C%20editorial%20style%20with%20quiet%20elegance&width=1600&height=600&seq=terms-hero-2026&orientation=landscape"
            alt="利用規約"
            title="利用規約"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 40%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/35"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span
              className="text-background-50/80 text-xs tracking-[0.4em] mb-6"
              style={{ fontFamily: "var(--font-label)" }}
            >
              TERMS OF SERVICE
            </span>
            <h1
              className="text-background-50 text-3xl md:text-5xl leading-[1.05] max-w-4xl whitespace-nowrap"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              利用規約
            </h1>
          </div>
        </section>

        {/* Terms Content */}
        <section className="w-full px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-foreground-600 leading-relaxed mb-12">
              この利用規約（以下「本規約」といいます）は、ハヤブサワークス（以下「当方」といいます）が提供するウェブサイト（以下「本サイト」といいます）の利用条件を定めるものです。ユーザーの皆さま（以下「ユーザー」といいます）には、本規約に同意のうえ本サイトをご利用いただくものとします。
            </p>

            {/* Section 1 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第1条（適用範囲）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本規約は、ユーザーと当方との間における本サイトの利用に関する一切の関係に適用されます。本サイト上に別途規定する個別の利用条件や注意事項がある場合、それらも本規約の一部を構成するものとします。
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第2条（禁止事項）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                ユーザーは、本サイトの利用にあたり、以下の行為を行ってはなりません。
              </p>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                {[
                  "法令または公序良俗に違反する行為",
                  "犯罪行為に関連する行為",
                  "当方のサーバーまたはネットワークの機能を破壊・妨害する行為",
                  "当方のサービスの運営を妨害するおそれのある行為",
                  "他のユーザーに関する個人情報等を収集・蓄積する行為",
                  "他のユーザーに成りすます行為",
                  "当方のサービスに関連して、反社会的勢力に対して直接または間接に利益を供与する行為",
                  "本サイトに掲載されたコンテンツ（文章、画像、動画等）を無断で転載、複製、改変する行為",
                  "その他、当方が不適切と判断する行為",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-primary-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第3条（本サイトの提供の停止等）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方は、以下のいずれかの事由があると判断した場合、ユーザーに事前に通知することなく本サイトの全部または一部の提供を停止または中断することができるものとします。
              </p>
              <ul className="space-y-2.5 mt-4 text-sm text-foreground-600 leading-relaxed">
                {[
                  "本サイトにかかるコンピュータシステムの保守点検または更新を行う場合",
                  "地震、落雷、火災、停電、天災等の不可抗力により本サイトの提供が困難となった場合",
                  "コンピュータまたは通信回線等が事故により停止した場合",
                  "その他、当方が本サイトの提供が困難と判断した場合",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-accent-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                当方は、本サイトの提供の停止または中断により、ユーザーまたは第三者が被ったいかなる不利益または損害についても、一切の責任を負わないものとします。
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第4条（著作権・知的財産権）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本サイトに掲載されているすべてのコンテンツ（文章、画像、動画、ロゴ、デザイン、商品写真等）の著作権および知的財産権は、当方または正当な権利を有する第三者に帰属します。ユーザーは、これらのコンテンツを当方の事前の承諾なく複製、転載、改変、配布等することはできません。
              </p>
            </div>

            {/* Section 5 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第5条（免責事項）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                当方は、本サイトに掲載する情報について、その正確性、有用性、完全性、最新性等を保証するものではありません。以下の事項について、当方は一切の責任を負いません。
              </p>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                {[
                  "本サイトに掲載された情報に基づいてユーザーが行った判断および行動により生じた損害",
                  "本サイトの利用または利用不能により生じた損害",
                  "本サイトからリンクされた外部サイトの内容およびそれにより生じた損害",
                  "本サイトに掲載された商品情報と実際の商品との差異",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-secondary-500 text-background-50 text-[10px]">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 6 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第6条（商品の購入について）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本サイトからリンクされた公式オンラインショップ（STORES）における商品の購入については、当該オンラインショップの利用規約および特定商取引法に基づく表記に従うものとします。商品の価格、送料、支払方法、発送時期等の詳細は、公式オンラインショップの表示に準じます。
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第7条（ユーザー情報の取扱い）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方によるユーザーの個人情報の取扱いについては、別途定めるプライバシーポリシーの定めによるものとし、ユーザーは当該ポリシーに従って当方がユーザーの個人情報を取扱うことについて同意するものとします。
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第8条（規約の変更）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                当方は、必要と判断した場合には、ユーザーに通知することなく本規約を変更することができるものとします。変更後の本規約は、本サイトに掲載された時点で効力を生じるものとし、ユーザーが変更後に本サイトを利用した場合、変更後の規約に同意したものとみなされます。
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-12">
              <h2
                className="text-foreground-950 text-xl mb-5 pb-3 border-b border-background-200/60"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                第9条（準拠法・管轄裁判所）
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本規約の解釈にあたっては、日本法を準拠法とします。本サイトに関して紛争が生じた場合には、松江地方裁判所を第一審の専属的合意管轄裁判所とします。
              </p>
            </div>

            {/* Enactment Date */}
            <div className="pt-6 border-t border-background-200/60 text-right">
              <p className="text-xs text-foreground-600">
                制定日：2026年7月14日
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}